/**
 * Selector hooks over the per-session rule store. Field-grained subscriptions
 * (via Zustand selectors) so a value/derived-state change only re-renders the
 * components that read that slice.
 */
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import type { QualifiedId, Scope } from '../../config/fieldRulesConfig';
import type { FieldUiState, OptionResolvers } from '../types';
import { deriveFieldStatesForScope } from '../engine/deriveFieldStates';
import { useRuleStoreApi } from './FieldRulesProvider';
import type { RuleEngineState } from './createRuleStore';

const EMPTY_RESOLVERS: OptionResolvers = {};
const DEFAULT_FIELD_STATE: FieldUiState = { visible: true, disabled: false, required: false, applicable: true };

export function useRuleStore<T>(selector: (state: RuleEngineState) => T): T {
    const store = useRuleStoreApi();
    return useStore(store, selector);
}

/** Subscribes to a single field's raw value. */
export function useFieldValue(qid: QualifiedId): unknown {
    const store = useRuleStoreApi();
    return useStore(store, (s) => s.values[qid]);
}

/** Subscribes to a scope's values as a bare-keyed object (shallow-compared). */
export function useScopeValues(scope: Scope): Record<string, unknown> {
    const store = useRuleStoreApi();
    const prefix = `${scope}:`;
    return useStoreWithEqualityFn(
        store,
        (s) => {
            const out: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(s.values)) {
                if (key.startsWith(prefix)) out[key.slice(prefix.length)] = value;
            }
            return out;
        },
        shallow,
    );
}

/**
 * Derives the full UI-state map for a scope; recomputes when values/ctx/rules change.
 * For `promo` with an `instanceId`, evaluates that instance against a MERGED view
 * (global event/plan values + the instance's promo values, ctx + instance hasEntityId)
 * so each promo tab is isolated while cross-section rules still see event/plan data.
 */
export function useScopeFieldStates(
    scope: Scope,
    resolvers: OptionResolvers = EMPTY_RESOLVERS,
    instanceId?: string,
): Record<string, FieldUiState> {
    const store = useRuleStoreApi();
    const values = useStore(store, (s) => s.values);
    const ctx = useStore(store, (s) => s.ctx);
    const rules = useStore(store, (s) => s.rules);
    const instance = useStore(store, (s) => (instanceId ? s.promo[instanceId] : undefined));
    return useMemo(() => {
        if (scope === 'promo' && instanceId) {
            const merged = { ...values, ...(instance?.values ?? {}) };
            const mergedCtx = { ...ctx, hasEntityId: instance?.hasEntityId };
            return deriveFieldStatesForScope(rules, scope, merged, mergedCtx, resolvers);
        }
        return deriveFieldStatesForScope(rules, scope, values, ctx, resolvers);
    }, [rules, scope, values, ctx, resolvers, instance, instanceId]);
}

/** Derived UI state for a single field (defaults when no rule targets it). */
export function useFieldUiState(
    scope: Scope,
    name: string,
    resolvers?: OptionResolvers,
    instanceId?: string,
): FieldUiState {
    const states = useScopeFieldStates(scope, resolvers, instanceId);
    return states[name] ?? DEFAULT_FIELD_STATE;
}
